import React, { useState } from 'react';
import { workflows } from './workflowData';

// Version row component
function VersionRow({ version, workflow, isActive, onClick }) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onClick({ ...workflow, selectedVersion: version });
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '12px 20px 12px 72px',
        borderBottom: '1px solid #f3f4f6',
        background: isHovered ? '#f9fafb' : '#fafafa',
        cursor: 'pointer',
        transition: 'background 0.15s ease'
      }}
    >
      {/* Version badge */}
      <div style={{
        width: '40px',
        height: '28px',
        borderRadius: '6px',
        background: '#fff',
        border: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: '12px',
        fontSize: '12px',
        fontWeight: 600,
        color: '#374151'
      }}>
        {version.number}
      </div>

      {/* Active badge */}
      {isActive && (
        <span style={{
          fontSize: '10px',
          padding: '2px 8px',
          borderRadius: '4px',
          background: '#dbeafe',
          color: '#2563eb',
          fontWeight: 500,
          marginRight: '12px'
        }}>
          Active
        </span>
      )}

      {/* Score */}
      <div style={{ minWidth: '60px', marginRight: '16px' }}>
        <span style={{
          fontSize: '14px',
          fontWeight: 600,
          color: version.score < 0.03 ? '#16a34a' : version.score < 0.06 ? '#ca8a04' : '#dc2626'
        }}>
          {version.score.toFixed(3)}
        </span>
        <span style={{ fontSize: '10px', color: '#9ca3af', marginLeft: '4px' }}>r/t</span>
      </div>

      {/* Push stats */}
      <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto', fontSize: '12px', color: '#6b7280' }}>
        <span>{version.pushes} pushes</span>
        <span style={{ color: '#16a34a' }}>{version.cleanPushes} clean</span>
        <span style={{ color: '#dc2626' }}>{version.revertedPushes} reverted</span>
      </div>

      {/* Arrow */}
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#9ca3af" style={{ width: '16px', height: '16px', marginLeft: '12px' }}>
        <path fillRule="evenodd" d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
      </svg>
    </div>
  );
}

function WorkflowItem({ workflow, onMenuClick, onClick, isExpanded, onToggleExpand }) {
  const [isHovered, setIsHovered] = useState(false);

  // Get score color
  const getScoreColor = (score) => {
    if (score < 0.03) return '#16a34a';
    if (score < 0.06) return '#ca8a04';
    return '#dc2626';
  };

  // Get trend icon
  const getTrendIcon = (trend) => {
    if (trend === 'improving') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#16a34a" style={{ width: '14px', height: '14px' }}>
          <path fillRule="evenodd" d="M10 17a.75.75 0 0 1-.75-.75V5.612L5.29 9.77a.75.75 0 0 1-1.08-1.04l5.25-5.5a.75.75 0 0 1 1.08 0l5.25 5.5a.75.75 0 1 1-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0 1 10 17Z" clipRule="evenodd" />
        </svg>
      );
    } else if (trend === 'declining') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#dc2626" style={{ width: '14px', height: '14px' }}>
          <path fillRule="evenodd" d="M10 3a.75.75 0 0 1 .75.75v10.638l3.96-4.158a.75.75 0 1 1 1.08 1.04l-5.25 5.5a.75.75 0 0 1-1.08 0l-5.25-5.5a.75.75 0 1 1 1.08-1.04l3.96 4.158V3.75A.75.75 0 0 1 10 3Z" clipRule="evenodd" />
        </svg>
      );
    }
    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#6b7280" style={{ width: '14px', height: '14px' }}>
        <path fillRule="evenodd" d="M4 10a.75.75 0 0 1 .75-.75h10.5a.75.75 0 0 1 0 1.5H4.75A.75.75 0 0 1 4 10Z" clipRule="evenodd" />
      </svg>
    );
  };

  // Sort versions with active first
  const sortedVersions = [...(workflow.evaluation?.versions || [])].sort((a, b) => {
    if (a.status === 'active' && b.status !== 'active') return -1;
    if (a.status !== 'active' && b.status === 'active') return 1;
    return 0;
  });

  return (
    <div>
      <div
        onClick={() => onToggleExpand(workflow.id)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: isExpanded ? 'none' : '1px solid #f3f4f6',
          background: isHovered ? '#fafafa' : '#ffffff',
          cursor: 'pointer',
          transition: 'background 0.15s ease'
        }}
      >
        {/* Expand/Collapse Arrow */}
        <div style={{
          width: '20px',
          height: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: '12px',
          transition: 'transform 0.2s'
        }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="#9ca3af"
            style={{
              width: '16px',
              height: '16px',
              transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s'
            }}
          >
            <path fillRule="evenodd" d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
          </svg>
        </div>

        {/* Workflow Icon */}
        <div style={{
          width: '40px',
          height: '40px',
          background: workflow.evaluation.currentScore < 0.05 ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: '16px'
        }}>
          <span style={{ fontSize: '20px' }}>🤖</span>
        </div>

        {/* Name & Info */}
        <div style={{ flex: 1 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '4px'
          }}>
            <span style={{
              fontWeight: 600,
              fontSize: '14px',
              color: '#111827'
            }}>
              {workflow.name}
            </span>
            <span style={{
              fontSize: '11px',
              padding: '2px 8px',
              borderRadius: '4px',
              background: '#f3f4f6',
              color: '#6b7280',
              fontWeight: 500
            }}>
              {workflow.currentVersion}
            </span>
            {/* In Chat Badge */}
            {workflow.inChat && (
              <span style={{
                fontSize: '10px',
                padding: '2px 8px',
                borderRadius: '4px',
                background: '#dcfce7',
                color: '#16a34a',
                fontWeight: 500
              }}>
                In Chat
              </span>
            )}
            {workflow.tags.slice(0, 2).map(tag => (
              <span
                key={tag}
                style={{
                  background: '#f3f4f6',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  color: '#6b7280'
                }}
              >
                {tag}
              </span>
            ))}
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '12px',
            color: '#6b7280'
          }}>
            <span>{workflow.steps?.length || 0} steps</span>
            <span style={{ color: '#d1d5db' }}>|</span>
            <span>{(workflow.evaluation?.versions || []).length} versions</span>
            <span style={{ color: '#d1d5db' }}>|</span>
            <span>{workflow.evaluation?.totalLifetimePushes || 0} pushes</span>
          </div>
        </div>

        {/* Score & Trend */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginRight: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {getTrendIcon(workflow.evaluation?.trend || 'stable')}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{
              fontSize: '16px',
              fontWeight: 600,
              color: getScoreColor(workflow.evaluation?.currentScore || 0)
            }}>
              {(workflow.evaluation?.revertsPerToken ?? workflow.evaluation?.currentScore ?? 0).toFixed(3)}
            </div>
            <div style={{ fontSize: '10px', color: '#9ca3af' }}>reverts/token</div>
          </div>
        </div>

        {/* Menu Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onMenuClick(workflow);
          }}
          style={{
            background: 'transparent',
            border: 'none',
            padding: '8px',
            cursor: 'pointer',
            borderRadius: '4px',
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.15s ease'
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#6b7280" style={{ width: '20px', height: '20px' }}>
            <path d="M10 3a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM10 8.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM11.5 15.5a1.5 1.5 0 1 0-3 0 1.5 1.5 0 0 0 3 0Z" />
          </svg>
        </button>
      </div>

      {/* Expanded Versions */}
      {isExpanded && (
        <div style={{ background: '#fafafa', borderBottom: '1px solid #e5e7eb' }}>
          {sortedVersions.map(version => (
            <VersionRow
              key={version.id}
              version={version}
              workflow={workflow}
              isActive={version.status === 'active'}
              onClick={onClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function WorkflowList({ searchQuery, onWorkflowClick }) {
  const [expandedWorkflows, setExpandedWorkflows] = useState({});

  const filteredWorkflows = workflows.filter(workflow =>
    workflow.name.toLowerCase().includes((searchQuery || '').toLowerCase())
  );

  const handleMenuClick = (workflow) => {
    // Menu click handler
  };

  const handleToggleExpand = (workflowId) => {
    setExpandedWorkflows(prev => ({
      ...prev,
      [workflowId]: !prev[workflowId]
    }));
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: '12px 20px',
        borderBottom: '1px solid #e5e7eb',
        background: '#fafafa',
        fontSize: '11px',
        fontWeight: 600,
        color: '#6b7280',
        textTransform: 'uppercase',
        letterSpacing: '0.05em'
      }}>
        <div style={{ width: '32px' }}></div>
        <div style={{ flex: 1 }}>Agent</div>
        <div style={{ width: '100px', textAlign: 'right', marginRight: '60px' }}>Score</div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {filteredWorkflows.map(workflow => (
          <WorkflowItem
            key={workflow.id}
            workflow={workflow}
            onMenuClick={handleMenuClick}
            onClick={onWorkflowClick}
            isExpanded={expandedWorkflows[workflow.id]}
            onToggleExpand={handleToggleExpand}
          />
        ))}
        {filteredWorkflows.length === 0 && (
          <div style={{
            padding: '40px',
            textAlign: 'center',
            color: '#6b7280',
            fontSize: '14px'
          }}>
            No agents found
          </div>
        )}
      </div>

      <div style={{
        padding: '12px 20px',
        borderTop: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: '#fafafa',
        fontSize: '12px',
        color: '#6b7280'
      }}>
        <span>
          {filteredWorkflows.length} agent{filteredWorkflows.length !== 1 ? 's' : ''}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>Lower reverts/token is better</span>
          <span style={{ color: '#d1d5db' }}>|</span>
          <span style={{ color: '#16a34a' }}>&lt;0.03</span>
          <span style={{ color: '#ca8a04' }}>0.03-0.06</span>
          <span style={{ color: '#dc2626' }}>&gt;0.06</span>
        </div>
      </div>
    </div>
  );
}

export default WorkflowList;
